import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Progress,
  Statistic,
  Alert,
  Button,
  Space,
  Tag,
  Descriptions,
  List,
  message
} from 'antd';
import {
  MonitorOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  ClockCircleOutlined,
  HddOutlined
} from '@ant-design/icons';
import { adminService, SystemHealth } from '../../../services/adminService';

const SystemMonitor: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    loadSystemHealth();
    // 每30秒自動刷新
    const interval = setInterval(loadSystemHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSystemHealth = async () => {
    try {
      setLoading(true);
      const response = await adminService.getSystemHealth();
      if (response.success && response.data) {
        setSystemHealth(response.data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('載入系統健康狀態失敗:', error);
      message.error('載入系統健康狀態失敗');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'warning':
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <MonitorOutlined />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return '#52c41a';
      case 'warning': return '#faad14';
      case 'error': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'healthy': return '正常';
      case 'warning': return '警告';
      case 'error': return '錯誤';
      default: return '未知';
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}天 ${hours}小時 ${minutes}分鐘`;
  };

  const getMemoryUsagePercent = (memoryUsage: any) => {
    if (!memoryUsage) return 0;
    return Math.round((memoryUsage.heapUsed / memoryUsage.heapTotal) * 100);
  };

  return (
    <div className="system-monitor">
      {/* 頭部操作區 */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <MonitorOutlined style={{ fontSize: 20 }} />
          <span style={{ fontSize: 16, fontWeight: 500 }}>系統監控</span>
          {lastUpdate && (
            <span style={{ color: '#666', fontSize: 12 }}>
              最後更新: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
        </Space>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={loadSystemHealth}
          loading={loading}
        >
          刷新
        </Button>
      </div>

      {/* 系統狀態總覽 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="系統狀態"
              value={systemHealth ? getStatusText(systemHealth.status) : '載入中...'}
              prefix={systemHealth ? getStatusIcon(systemHealth.status) : <MonitorOutlined />}
              valueStyle={{ color: systemHealth ? getStatusColor(systemHealth.status) : '#666' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="運行時間"
              value={systemHealth ? formatUptime(systemHealth.checks.uptime) : '0分鐘'}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="活躍連接"
              value={systemHealth?.checks.activeConnections || 0}
              prefix={<DesktopOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="數據庫"
              value={systemHealth?.checks.database ? '正常' : '異常'}
              prefix={<DatabaseOutlined />}
              valueStyle={{ 
                color: systemHealth?.checks.database ? '#52c41a' : '#ff4d4f' 
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* 內存使用情況 */}
      {systemHealth?.checks.memoryUsage && (
        <Card title="內存使用情況" style={{ marginBottom: 24 }}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>堆內存使用率</span>
                  <span>{getMemoryUsagePercent(systemHealth.checks.memoryUsage)}%</span>
                </div>
                <Progress
                  percent={getMemoryUsagePercent(systemHealth.checks.memoryUsage)}
                  status={getMemoryUsagePercent(systemHealth.checks.memoryUsage) > 80 ? 'exception' : 'normal'}
                />
              </div>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="已使用堆內存">
                  {formatBytes(systemHealth.checks.memoryUsage.heapUsed)}
                </Descriptions.Item>
                <Descriptions.Item label="總堆內存">
                  {formatBytes(systemHealth.checks.memoryUsage.heapTotal)}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col xs={24} md={12}>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="常駐內存">
                  {formatBytes(systemHealth.checks.memoryUsage.rss)}
                </Descriptions.Item>
                <Descriptions.Item label="外部內存">
                  {formatBytes(systemHealth.checks.memoryUsage.external)}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Card>
      )}

      {/* 停車場指標 */}
      {systemHealth?.metrics && (
        <Card title="停車場指標" style={{ marginBottom: 24 }}>
          <Row gutter={24}>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="總停車位"
                value={systemHealth.metrics.totalSpots}
                prefix={<DatabaseOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="已佔用"
                value={systemHealth.metrics.occupiedSpots}
                suffix={`/ ${systemHealth.metrics.totalSpots}`}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <div style={{ marginBottom: 8 }}>使用率</div>
                <Progress
                  type="circle"
                  percent={parseFloat(systemHealth.metrics.occupancyRate)}
                  size={80}
                  status={parseFloat(systemHealth.metrics.occupancyRate) > 90 ? 'exception' : 'normal'}
                />
              </div>
            </Col>
          </Row>
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col xs={24} sm={12}>
              <Statistic
                title="逾期會話"
                value={systemHealth.metrics.overdueSessions}
                valueStyle={{ 
                  color: systemHealth.metrics.overdueSessions > 10 ? '#ff4d4f' : '#52c41a' 
                }}
              />
            </Col>
            <Col xs={24} sm={12}>
              <Statistic
                title="維護中停車位"
                value={systemHealth.metrics.maintenanceSpots}
                valueStyle={{ 
                  color: systemHealth.metrics.maintenanceSpots > 20 ? '#faad14' : '#52c41a' 
                }}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* 系統問題 */}
      {systemHealth?.issues && systemHealth.issues.length > 0 && (
        <Card title="系統問題" style={{ marginBottom: 24 }}>
          <List
            dataSource={systemHealth.issues}
            renderItem={(issue, index) => (
              <List.Item>
                <Alert
                  message={issue}
                  type="warning"
                  showIcon
                  style={{ width: '100%' }}
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* 系統正常提示 */}
      {systemHealth?.status === 'healthy' && (!systemHealth.issues || systemHealth.issues.length === 0) && (
        <Card>
          <Alert
            message="系統運行正常"
            description="所有系統組件運行正常，沒有發現問題。"
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        </Card>
      )}
    </div>
  );
};

export default SystemMonitor;